import TextField from '@mui/material/TextField'
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import { makeStyles } from '@mui/styles'
import CloseIcon from '@mui/icons-material/Close'
import { CATEGORY_CONFIG, PORTFOLIO_CARD, API_METHODS, ADD_ICON as AddIcon, Category } from './dashboardConfig'

const useStyles = makeStyles(() => ({
  apiDialogPaper: {
    borderRadius: 16,
    overflow: 'hidden',
  },
  apiDialogHeader: {
    background: 'linear-gradient(90deg, #7C3AED 0%, #4F46E5 100%)',
    color: '#fff',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  apiDialogContent: {
    padding: 20,
  },
  apiTabs: {
    borderBottom: '1px solid #E5E7EB',
    '& .MuiTab-root': {
      textTransform: 'none',
      fontWeight: 700,
      fontSize: 14,
    },
    '& .Mui-selected': {
      color: '#7C3AED',
    },
    '& .MuiTabs-indicator': {
      backgroundColor: '#7C3AED',
    },
  },
  apiSection: {
    marginBottom: 24,
  },
  apiSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 16,
    fontWeight: 700,
    color: '#7C3AED',
    borderBottom: '1px solid rgba(124,58,237,0.2)',
    paddingBottom: 8,
    marginBottom: 12,
  },
  apiRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#fff',
    border: '1px solid #EEE4C7',
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  apiRowAction: {
    background: '#F0FDF4',
    borderColor: '#BBF7D0',
  },
  apiRowIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  apiRowIconAction: {
    background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
  },
  apiRowLabel: {
    minWidth: 160,
    fontSize: 13,
    fontWeight: 600,
    color: '#4B5563',
  },
  apiRowFields: {
    display: 'flex',
    gap: 10,
    flex: 1,
  },
  apiActions: {
    padding: '12px 20px 20px',
    borderTop: '1px solid #E5E7EB',
  },
  apiMethod: {
    minWidth: 110,
  },
}))

export interface ApiManagerDialogProps {
  open: boolean
  onClose: () => void
  onSave?: () => void
}

const ApiManagerDialog: React.FC<ApiManagerDialogProps> = ({ open, onClose, onSave }) => {
  const classes = useStyles()
  const [activeTab, setActiveTab] = useState(0)
  const handleSave = onSave ?? onClose

  const config: Category[] = activeTab === 0 ? CATEGORY_CONFIG : [PORTFOLIO_CARD]

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth PaperProps={{ className: classes.apiDialogPaper }}>
      <Box className={classes.apiDialogHeader}>
        <Typography variant="h6">API Manager</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Tabs value={activeTab} onChange={(_, val) => setActiveTab(val)} className={classes.apiTabs}>
        <Tab label="Building" />
        <Tab label="Portfolio" />
      </Tabs>
      <DialogContent className={classes.apiDialogContent}>
        {config.map((category) => (
          <Box key={category.title} className={classes.apiSection}>
            <Typography className={classes.apiSectionTitle}>
              <category.icon fontSize="small" />
              {category.title}
            </Typography>
            {category.items.map((item) => (
              <Box key={item.name} className={classes.apiRow}>
                <Box className={classes.apiRowIcon}>
                  <item.icon fontSize="small" />
                </Box>
                <Typography className={classes.apiRowLabel}>{item.name}</Typography>
                <Box className={classes.apiRowFields}>
                  <TextField select size="small" defaultValue="GET" className={classes.apiMethod}>
                    {API_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField fullWidth size="small" placeholder="https://api.example.com/endpoint" />
                </Box>
              </Box>
            ))}
            {category.addButtonLabel && (
              <Box className={`${classes.apiRow} ${classes.apiRowAction}`}>
                <Box className={`${classes.apiRowIcon} ${classes.apiRowIconAction}`}>
                  <AddIcon fontSize="small" />
                </Box>
                <Typography className={classes.apiRowLabel}>+ {category.addButtonLabel}</Typography>
                <Box className={classes.apiRowFields}>
                  <TextField select size="small" defaultValue="POST" className={classes.apiMethod}>
                    {API_METHODS.map((method) => (
                      <MenuItem key={method} value={method}>
                        {method}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField fullWidth size="small" placeholder="https://api.example.com/create" />
                </Box>
              </Box>
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions className={classes.apiActions}>
        <Button onClick={onClose} sx={{ color: '#7C3AED', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          sx={{
            background: 'linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)',
            fontWeight: 600,
            '&:hover': {
              background: 'linear-gradient(135deg, #6D28D9 0%, #4338CA 100%)',
            },
          }}
        >
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ApiManagerDialog
